import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaFileAlt, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import materialMaintenanceService from "../../../../../services/web/material-maintenance-service.js";
import projectUtils from "../../../../../utils/project-utils.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import {unavailableReasonLabels} from "../utils/maintenances-utils.jsx";

const initialFilter = {
    material: "",
}

const MaterialMaintenancesList = () => {
    const navigate = navigateHook();
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);

    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        {field: "date_send", headerName: "Envio",  width: 145, sort: "desc", valueFormatter: ({value}) => projectUtils.formatDate(value), comparator: aggridUtils.agGridCompareDateTime},
        {field: "return_estimate", headerName: "Estimativa de retorno",  width: 180,  valueFormatter: ({value}) => projectUtils.formatDate(value, "DD/MM/YYYY"), comparator: aggridUtils.agGridSimpleDateComparator},
        {field: "date_return", headerName: "Retorno",  width: 145,  valueFormatter: ({value}) => projectUtils.formatDate(value), comparator: aggridUtils.agGridCompareDateTime},
        {field: "storage", headerName: "Depósito", flex: 1, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "group", headerName: "Grupo", flex: 1, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "material", headerName: "Material", flex:1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.lowerUpCaseComparator},
        {field: "unavailable_reason", headerName: "Motivo", valueFormatter: ({ value }) => unavailableReasonLabels[value] ?? value ?? "",
            comparator: (valueA, valueB) => {
                const a = unavailableReasonLabels[valueA] ?? valueA ?? "";
                const b = unavailableReasonLabels[valueB] ?? valueB ?? "";

                return a.localeCompare(b, "pt-BR");
            }
        },
    ], []);

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const { data: maintenances = [], isLoading } = useQuery({
        queryKey: ['materials-maintenances'],
        queryFn: async () => {
            const { data } = await materialMaintenanceService.list();
            return data;
        },
    });

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = ({event, data}) => {
        const id = data.id;
        const urlDev = `/materials/maintenances/form/${id}`;
        let isBlank = false
        if (event.ctrlKey) isBlank = true
        navigate(urlDev, isBlank)
    }

    const openForm = () => {
        const urlDev = `/materials/maintenances/form`;
        navigate(urlDev)
    }

    const openReport = () => {
        const url = `/reports/material_movements?operation=maintenance_send`;
        navigate(url, true)
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Indisponibilidade de materiais
                </Template.Title>
                <div className={"flex gap-2"}>
                    <Button onClick={openReport}>
                        <FaFileAlt/><span className="font-bold">Relatório</span>
                    </Button>
                    <Button onClick={openForm}>
                        <FaPlus/><span className="font-bold">Adicionar indisponibilidade</span>
                    </Button>
                </div>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className="col-span-12 flex gap-4">
                                <InputForm id="material" name="material" value={filter.material} onChange={onChange}
                                           label="Material" classNameWrapper={"w-full"}/>
                                <div className="flex justify-start gap-2">
                                    <Button target="primary-outline" onClick={clearFilter} title={"Limpar"}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search} title={"Buscar"}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={maintenances}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialMaintenancesList;
